/**
 * Email Preferences Handler
 * Manages customer email preferences and updates metafields and tags
 */

class EmailPreferencesHandler {
  constructor() {
    this.section = document.querySelector('.email-preferences-section');
    this.form = this.section ? this.section.querySelector('.preferences-form') : null;
    this.unsubscribeCheckbox = document.getElementById('unsubscribe-all-checkbox');
    this.saveButton = this.section ? this.section.querySelector('.save-button') : null;
    this.isSubmitting = false;

    const urlParams = new URLSearchParams(window.location.search);
    this.emailFromUrl = urlParams.get('email');
    this.customerId = this.section ? this.section.dataset.customerId : null;
    
    if (!this.form || !this.section) return;
    
    this.initialize();
  }

  initialize() {
    // Prevent default form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleFormSubmit();
    });

    // Handle unsubscribe checkbox change
    if (this.unsubscribeCheckbox) {
      this.unsubscribeCheckbox.addEventListener('change', (e) => {
        this.handleUnsubscribeChange(e.target.checked);
      });
    }

    // Load existing preferences if customer is logged in
    this.loadExistingPreferences();
  }

  handleUnsubscribeChange(isChecked) {
    const preferenceCheckboxes = this.form.querySelectorAll('input[type="checkbox"][name^="preference_"]');
    
    if (isChecked) {
      // If unsubscribing, disable all preference checkboxes
      preferenceCheckboxes.forEach(checkbox => {
        checkbox.disabled = true;
        checkbox.checked = false;
      });
    } else {
      // If re-subscribing, enable all preference checkboxes
      preferenceCheckboxes.forEach(checkbox => {
        checkbox.disabled = false;
      });
    }
  }

  /**
   * Load existing customer preferences from metafield
   */
  async loadExistingPreferences() {
    // Check if customer data is available via data attribute on section
    const customerData = this.section ? this.section.dataset.customerPreferences : null;
    if (customerData) {
      try {
        const preferences = JSON.parse(customerData);
        this.populateForm(preferences);
      } catch (error) {
        console.log('Could not parse existing preferences:', error);
      }
    }
  }

  /**
   * Populate form with existing preferences
   */
  populateForm(preferences) {
    if (preferences.unsubscribed) {
      if (this.unsubscribeCheckbox) {
        this.unsubscribeCheckbox.checked = true;
        this.handleUnsubscribeChange(true);
      }
      return;
    }

    const preference1 = this.form.querySelector('input[name="preference_1"]');
    const preference2 = this.form.querySelector('input[name="preference_2"]');
    const preference3 = this.form.querySelector('input[name="preference_3"]');

    if (preference1) preference1.checked = preferences.save_date || false;
    if (preference2) preference2.checked = preferences.important_events || false;
    if (preference3) preference3.checked = preferences.special_offers || false;
  }

  /**
   * Determine which tags to apply based on selected preferences
   */
  determineTags(preferences) {
    const tags = [];
    
    if (preferences.unsubscribed) {
      tags.push('no-marketing');
      return tags;
    }

    const { save_date, important_events, special_offers } = preferences;
    const selectedCount = [save_date, important_events, special_offers].filter(Boolean).length;

    // If all 3 are selected, apply "all-events" tag
    if (selectedCount === 3) {
      tags.push('all-events');
    } else {
      // Otherwise, apply individual tags
      if (save_date) tags.push('save-date');
      if (important_events) tags.push('important-events');
      if (special_offers) tags.push('special-offers');
    }

    return tags;
  }

  /**
   * Handle form submission
   */
  async handleFormSubmit() {
    if (this.isSubmitting) return;

    if (!this.customerId && !this.emailFromUrl) {
      alert('Please log in or use the link from your email to save preferences.');
      return;
    }

    this.isSubmitting = true;
    this.saveButton.disabled = true;
    this.saveButton.textContent = 'Saving...';

    try {
      // Get form data
      const preferences = this.getFormData();
      
      // Determine tags
      const tags = this.determineTags(preferences);

      // Prepare data for API
      const payload = {
        email_preferences: {
          save_date: preferences.save_date,
          important_events: preferences.important_events,
          special_offers: preferences.special_offers,
          unsubscribed: preferences.unsubscribed,
          last_updated: new Date().toISOString()
        },
        tags: tags
      };

      // Send to endpoint
      const response = await this.updateCustomerPreferences(payload);

      if (response.success) {
        this.showSuccessMessage();
        // Optionally reload page after short delay to show updated state
        // setTimeout(() => {
        //   window.location.reload();
        // }, 1500);
      } else {
        throw new Error(response.error || 'Failed to save preferences');
      }

    } catch (error) {
      console.error('Error saving preferences:', error);
      this.showErrorMessage(error.message || 'An error occurred. Please try again.');
    } finally {
      this.isSubmitting = false;
      this.saveButton.disabled = false;
      // Restore original button text
      const originalText = this.saveButton.dataset.originalText || 'Save';
      this.saveButton.textContent = originalText;
    }
  }

  /**
   * Get form data
   */
  getFormData() {
    const isUnsubscribed = this.unsubscribeCheckbox ? this.unsubscribeCheckbox.checked : false;

    return {
      save_date: isUnsubscribed ? false : (this.form.querySelector('input[name="preference_1"]')?.checked || false),
      important_events: isUnsubscribed ? false : (this.form.querySelector('input[name="preference_2"]')?.checked || false),
      special_offers: isUnsubscribed ? false : (this.form.querySelector('input[name="preference_3"]')?.checked || false),
      unsubscribed: isUnsubscribed
    };
  }

  /**
   * Update customer preferences via Mechanic Webhook
   * Sends data to Mechanic webhook which then updates customer metafields and tags
   */
  async updateCustomerPreferences(payload) {
    // Mechanic webhook URL for email preferences updates
    const WEBHOOK_URL = 'https://webhooks.mechanic.dev/cda7e2f4-1aef-4996-a2c9-7ad896c3c719';
    
    if (!this.customerId && !this.emailFromUrl) {
      throw new Error('No customer identified');
    }

    const webhookData = {
      email_preferences: payload.email_preferences,
      tags: payload.tags,
      action: 'update_email_preferences'
    };

    if (this.customerId) {
      webhookData.customer_id = this.customerId;
    } else {
      webhookData.customer_email = this.emailFromUrl;
    }
    
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // Mechanic typically returns success
      return { success: true };
    } catch (error) {
      console.error('Webhook Error:', error);
      throw error;
    }
  }

  /**
   * Show success message
   */
  showSuccessMessage() {
    const messageDiv = document.createElement('div');
    messageDiv.className = 'email-preferences-message success';
    messageDiv.textContent = 'Your preferences have been saved successfully!';
    messageDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 15px 20px;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    document.body.appendChild(messageDiv);

    setTimeout(() => {
      messageDiv.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => messageDiv.remove(), 300);
    }, 3000);
  }

  /**
   * Show error message
   */
  showErrorMessage(message) {
    const messageDiv = document.createElement('div');
    messageDiv.className = 'email-preferences-message error';
    messageDiv.textContent = message;
    messageDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f44336;
      color: white;
      padding: 15px 20px;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    document.body.appendChild(messageDiv);

    setTimeout(() => {
      messageDiv.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => messageDiv.remove(), 300);
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new EmailPreferencesHandler();
  });
} else {
  new EmailPreferencesHandler();
}

